import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Notification from "../components/common/Notification"; // --- NEW IMPORT
import styles from "./AdminPage.module.css";

function AdminProductEditPage() {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isNew = location.pathname === "/admin/products/new";

  const [product, setProduct] = useState({
    id: "",
    name: "",
    price: "",
    category: "",
    description: "",
    minQuantity: 1,
    maxQuantity: 10,
  });

  const [categories, setCategories] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // --- NEW: Notification State ---
  const [notification, setNotification] = useState(null);
  const showToast = (message, type = "success") => {
    setNotification({ message, type });
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const loadPageData = async () => {
      setIsLoading(true);

      try {
        const categoriesRes = await fetch("/api/categories?type=Product");
        if (!categoriesRes.ok) throw new Error("Failed to load categories.");
        const categoriesData = await categoriesRes.json();
        setCategories(categoriesData);

        let defaultCategory = "";
        if (categoriesData.length > 0) {
          defaultCategory = categoriesData[0]._id;
        } else if (isNew) {
          showToast("No 'Product' categories found.", "error");
        }

        if (!isNew) {
          const productRes = await fetch(`/api/cms/products/${productId}`);
          if (!productRes.ok) throw new Error("Product not found.");

          const productData = await productRes.json();
          const priceInfo = [
            `${productData.price || 0}Br`,
            productData.description || "",
          ].join("\n");

          setProduct({
            id: productData.productID,
            name: productData.name,
            price: priceInfo,
            category: productData.category
              ? productData.category._id
              : defaultCategory,
            description: productData.description,
            minQuantity: productData.minQuantity || 1,
            maxQuantity: productData.maxQuantity || 10,
          });
          setExistingImageUrl(productData.imageURL);
        } else {
          setProduct((prev) => ({
            ...prev,
            category: defaultCategory,
            minQuantity: 1,
            maxQuantity: 10,
          }));
        }
      } catch (err) {
        console.error("Error loading page data:", err);
        showToast(err.message, "error");
      } finally {
        setIsLoading(false);
      }
    };

    loadPageData();
  }, [productId, isNew, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (Number(product.minQuantity) > Number(product.maxQuantity)) {
      showToast("Minimum quantity cannot be greater than Maximum.", "error");
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    const [priceLine, ...descLines] = product.price.split("\n");
    const priceValue = parseFloat(priceLine.replace(/[^0-9.]/g, "") || 0);
    const description = descLines.join("\n");

    formData.append("productID", product.id);
    formData.append("name", product.name);
    formData.append("category", product.category);
    formData.append("price", priceValue);
    formData.append("description", description);
    formData.append("minQuantity", product.minQuantity);
    formData.append("maxQuantity", product.maxQuantity);

    if (imageFile) {
      formData.append("image", imageFile);
    }

    const url = isNew ? "/api/cms/products" : `/api/cms/products/${productId}`;
    const method = isNew ? "POST" : "PUT";

    // --- GET KEY ---
    const adminKey = localStorage.getItem("adminKey");

    try {
      // --- SEND KEY ---
      const response = await fetch(url, {
        method: method,
        headers: {
          "x-admin-key": adminKey,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        // --- NEW: Show Toast & Redirect ---
        showToast(
          `Successfully ${isNew ? "created" : "updated"} product!`,
          "success"
        );
        setTimeout(() => {
          navigate("/admin/products");
        }, 1500);
      } else {
        showToast(`Failed: ${data.message || "Not authorized"}`, "error");
      }
    } catch (error) {
      console.error("API request failed:", error);
      showToast("Network Error: Could not connect to backend server.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.adminPage}>
      <Navbar />

      {/* --- RENDER NOTIFICATION --- */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className={styles.adminHero}>
        <h1>{isNew ? "Add New Product" : "Edit Product"}</h1>
      </div>

      <div className={styles.adminContentContainer}>
        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <form className={styles.adminEditForm} onSubmit={handleSubmit}>
            <div className={styles.formRowGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="id">Custom ID (SKU) *</label>
                <input
                  type="text"
                  id="id"
                  name="id"
                  value={product.id || ""}
                  onChange={handleInputChange}
                  required
                  disabled={!isNew}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="minQuantity">Min Order Quantity</label>
                <input
                  type="number"
                  id="minQuantity"
                  name="minQuantity"
                  value={product.minQuantity}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="maxQuantity">Max Order Quantity</label>
                <input
                  type="number"
                  id="maxQuantity"
                  name="maxQuantity"
                  value={product.maxQuantity}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="name">Product Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={product.name || ""}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={product.category || ""}
                onChange={handleInputChange}
                disabled={categories.length === 0}
              >
                {categories.length === 0 ? (
                  <option disabled>
                    {isLoading
                      ? "Loading..."
                      : "Please add a 'Product' category first."}
                  </option>
                ) : (
                  categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="price">
                Price (e.g., 7800Br) / Info (use \n for new lines)
              </label>
              <textarea
                id="price"
                name="price"
                rows="4"
                placeholder="e.g., 7800Br\nOrigin: Sidama\nProcess: Natural"
                value={product.price || ""}
                onChange={handleInputChange}
                required
              ></textarea>
              <p className={styles.formHint}>
                First line must contain the price. Subsequent lines become the
                description.
              </p>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="image">
                Product Image {isNew ? "*" : "(Optional: Upload to replace)"}
              </label>
              <input
                type="file"
                id="image"
                name="image"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleFileChange}
              />
              {!isNew && existingImageUrl && (
                <div style={{ marginTop: "10px" }}>
                  <p className={styles.formHint}>Current Image:</p>
                  <img
                    src={`${existingImageUrl}`}
                    alt="Existing product"
                    style={{
                      width: "100px",
                      height: "100px",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
            </div>

            <div className={styles.formActions}>
              <Link
                to="/admin/products"
                className={`${styles.adminBtn} ${styles.danger}`}
              >
                Cancel
              </Link>
              <button
                type="submit"
                className={`${styles.adminBtn} ${styles.primary}`}
                disabled={isLoading || categories.length === 0}
              >
                {isLoading
                  ? "Saving..."
                  : isNew
                  ? "Create Product"
                  : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default AdminProductEditPage;
